import { LCDocumentEditorUtils } from './LCDocumentEditorUtils';
import { ILectioPlugin } from './Types';

const sectionSlideClass = 'lc-document-slide';

export class PluginLectioSlide implements ILectioPlugin {
	public Init(): void {

		CKEDITOR.plugins.add('lectioslide', {
			requires: 'widget',

			// jscs:disable maximumLineLength
			//lang: 'af,ar,bg,bn,bs,ca,cs,cy,da,de,el,en,en-au,en-ca,en-gb,eo,es,et,eu,fa,fi,fo,fr,fr-ca,gl,gu,he,hi,hr,hu,id,is,it,ja,ka,km,ko,ku,lt,lv,mk,mn,ms,nb,nl,no,pl,pt,pt-br,ro,ru,si,sk,sl,sq,sr,sr-latn,sv,th,tr,tt,ug,uk,vi,zh,zh-cn', // %REMOVE_LINE_CORE%
			// jscs:enable maximumLineLength
			// icons: 'blockquote', // %REMOVE_LINE_CORE%
			hidpi: true, // %REMOVE_LINE_CORE%
			init: (editor: CKEDITOR.editor) => {
				if (editor.blockless)
					return;

				editor.widgets.add('lectioslide', {
					name: 'lectioslidename',
					template:
						'<section class="lc-document-slide">' +
						'</section>',

					editables: {
						content: {
							selector: '.lc-document-slide'
						}
					},

					upcast: (element: any) => element.name === 'section' && element.hasClass('lc-document-slide')
				});

				// Der skal i udgangspunktet ikke kunne indsaettes slides uden for slide mode, men vi loader plugin'et alligevel til
				// hvis der skulle snige sig slides ind, saa kan vises. Man kunne alternativt pakke indholdet af dem ud...
				const enableCreateSlides = LCDocumentEditorUtils.GetEditorConfig(editor).allowInsertSlides;
				// Registrerer kommandoen uanset aht. unit test.
				editor.addCommand('lectioslide', lectioSlideCommandObject);
				if (enableCreateSlides) {
					const cfg = LCDocumentEditorUtils.GetEditorConfig(editor);

					if (editor.ui.addButton) {
						editor.ui.addButton('LectioSlide', {
							label: 'Nyt slide/dias (Ctrl+Enter)',
							command: 'lectioslide',
							icon: CKEDITOR.skin.icons.newslide!.path,
							toolbar: ''
						});
					}

					editor.addCommand('insertnextlectioslide', insertNextLectioSlideCommandObject);
					editor.setKeystroke(CKEDITOR.CTRL + 13 /*enter*/, 'insertnextlectioslide');
				}

				editor.on('paste', onPaste);
			}
		});
	}
}

const insertNextLectioSlideCommandObject: Partial<CKEDITOR.commandDefinition> = {
	exec: (editor: CKEDITOR.editor) => {
		if (!editor)
			return false;

		const selection = editor.getSelection(),
			range = selection && selection.getRanges()[0];

		if (!range)
			return false;
		const parents = range.startContainer.getParents() as CKEDITOR.dom.node[];
		const isInsideSlide = parents.filter(n => {
			if (!(n instanceof CKEDITOR.dom.element))
				return false;
			return n.getName() === 'section' && n.hasClass(sectionSlideClass);
		}).length !== 0;
		if (!isInsideSlide)
			return false;

		if (!lectioSlideCommandObject.exec)
			throw new Error('hold nu op, typescript, der er ikke noget typeproblem her.');

		return lectioSlideCommandObject.exec(editor);
	}
};

const lectioSlideExec: CKEDITOR.commandDefinition['exec'] = (editor: CKEDITOR.editor) => {
	if (!editor)
		return false;

	const selection = editor.getSelection(),
		range = selection && selection.getRanges()[0];

	// Find det øverste element, givet cursoren, så vi kan indsætte slide efter det.
	let curpoint = !range || editor.editable()!.$ === range.startContainer.$ ? null : range.startContainer;
	while (curpoint && curpoint.getParent() && curpoint.getParent().getName() !== 'body' && !curpoint.getParent().hasClass('cke_editable'))
		curpoint = curpoint.getParent();
	if ((curpoint instanceof CKEDITOR.dom.text))
		throw new Error('sikken maerkelig dom.');

	// Man kan godt få en helt tom body ved at markere alt og slette det. I så fald er startContainer body.
	// Derudover vil man næsten altid stå inde i en container, ofte et p-element.
	const curTopLevelElement = curpoint && curpoint.getName() !== 'body' ? curpoint : null;
	const newSection = editor.document.createElement('section');
	newSection.addClass(sectionSlideClass);
	if (curTopLevelElement != null) {
		newSection.insertAfter(curTopLevelElement);

		// Hvis cursoren stod i et tomt p/h1/h2/h3-element, fjerner vi det.
		let remove = false;
		if (/^\s*$/.test(curTopLevelElement.getHtml()))
			remove = true;
		else if (curTopLevelElement.getChildCount() === 1 &&
			curTopLevelElement.getChild(0) instanceof CKEDITOR.dom.element &&
			(curTopLevelElement.getChild(0) as CKEDITOR.dom.element).getName() === 'br')
			remove = true;
		if (remove && !curTopLevelElement.hasAttribute('id'))
			curTopLevelElement.remove();
	}
	else
		editor.editable()!.append(newSection);
	editor.widgets.initOn(newSection, 'lectioslide', undefined);
	const slideClientRect = newSection.getClientRect();
	const windowHeight = window.innerHeight;
	if (slideClientRect.top + slideClientRect.height > windowHeight) {
		// Laegger en smule til i tilfaelde af at der er horisontal scrollbar.
		const below = slideClientRect.top + slideClientRect.height - windowHeight + 20;
		window.scrollBy(0, below);
	}

	// Flyt cursor ind i ny slide, så der straks kan skrives.
	const r2 = editor.createRange();
	r2.selectNodeContents(newSection);
	r2.select();

	editor.focus();
	return true;
};

const lectioSlideCommandObject: Partial<CKEDITOR.commandDefinition> & { allowedContent: string } = {
	exec: lectioSlideExec,

	allowedContent: 'section(lc-document-slide)'
};

function onPaste(evt: CKEDITOR.eventInfo): void {
	if (!evt || !evt.editor)
		return;
	const editor = evt.editor;

	// Når der paste eller trækkes section.ls-document-slide (slide(s)), vil vi gerne have fjernet slide-indpakning af det pastede indhold,
	// så vi ikke får slides indlejret i slides.
	const targetRange = evt.data.range as CKEDITOR.dom.range || editor.getSelection()!.getRanges()[0];
	if (!targetRange)
		return;
	const isInsideSlide = targetRange.startPath().elements.filter(e => e.getName() === 'section' && e.hasClass(sectionSlideClass));
	if (!isInsideSlide.length)
		return;

	// Parse the HTML string to pseudo-DOM structure.
	const fragmentHtml = evt.data.dataValue as string;
	const fragment = CKEDITOR.htmlParser.fragment.fromHtml(fragmentHtml),
		writer = new CKEDITOR.htmlParser.basicWriter();

	// a) Vi fjerner slide-indpakning manuelt, fordi ckeditor.filter afviser at arbejde paa widgets.
	// b) man burde nok ikke kun koere igennem paa oeverste niveau, for
	//   naar indhold saettes ind fra en visning, er det ikke usandsynligt at slides ikke er oeverste niveau.
	for (const c of fragment.children) {
		if (!(c instanceof CKEDITOR.htmlParser.element))
			continue;

		const e = c;
		if (e.hasClass('cke_widget_wrapper') && e.children.length >= 1 &&
			e.children[0] instanceof CKEDITOR.htmlParser.element &&
			(e.children[0] as CKEDITOR.htmlParser.element).name === 'section') {
			// indhold fra editor, hvor der div rundt om widgets.
			const section1 = e.children[0] as CKEDITOR.htmlParser.element;

			const children1 = section1.children;
			for (let j = children1.length; j;)
				children1[--j].insertAfter(c);

			e.remove();
		} else if (e.name === 'section' && e.hasClass(sectionSlideClass)) {
			// indhold fra udenfor editoren.
			const section2 = e;

			const children2 = section2.children;
			for (let j = children2.length; j;)
				children2[--j].insertAfter(c);

			e.remove();
		}
	}

	fragment.writeHtml(writer);
	evt.data.dataValue = writer.getHtml(false);
}
